import { useCallback, useMemo, useState } from 'react';

import type { FormInstance } from 'antd/es/form';
import type { Moment } from 'moment';
import { useIntl } from 'react-intl';

import ShiftService from '@src/services/ShiftService';

import { SHIFT_TYPES } from '../constants';
import { getShiftValidationConfig } from '../constants/shiftValidationConfig';
import type {
  ActiveDayValue,
  DayErrors,
  DayWarnings,
  HoursByWeek,
  ShiftErrorsState,
  ShiftValidationCustomOptions,
  ShiftWarningsState,
  ValidationMessage,
} from '../types';

const isStartBreakTimeMissing = (
  breakTime: number | null | undefined,
  startBreakTime: Moment | null | undefined,
) => {
  if (breakTime == null) return false;
  if (startBreakTime == null || startBreakTime === undefined) return true;
  if (typeof startBreakTime.isValid === 'function' && !startBreakTime.isValid()) return true;
  return false;
};

interface ShiftValidatorResult {
  warnings: DayWarnings;
  errors: DayErrors;
  storageType: string;
  dayKey?: string | number | null;
  weekKey?: string | number | null;
}

/**
 * Custom hook for managing shift form validations
 * @param {string} shiftType - Type of shift ('fixed', 'partTime', 'cycle', 'biweekly', 'shiftWork')
 * @param {Object} customOptions - Optional custom configuration to override defaults
 * @param {number} customOptions.maxWeeklyHours - Maximum weekly hours allowed for this shift type
 * @param {boolean} customOptions.validateMaxHoursAsError - Whether to treat max hours as error instead of warning
 * @returns {Object} Validation state and functions
 */
export const useShiftValidation = (
  shiftType = 'fixed',
  customOptions: ShiftValidationCustomOptions = {},
  form?: FormInstance,
) => {
  // Get configuration for the shift type
  const config = getShiftValidationConfig(shiftType);

  // Merge with custom options
  const {
    maxHoursWarningMessage = config?.messages?.warnings?.maxHours?.message,
    maxDaysWarningMessage = config?.messages?.warnings?.maxDays?.message,
    maxDailyHoursWarningMessage = config?.messages?.warnings?.maxDailyHours?.message,
    maxConsecutiveActiveDaysWarning = config?.messages?.warnings?.maxConsecutiveActiveDays?.message,

    breakTimeErrorMessage = config?.messages?.errors?.breakTime?.message,
    maxHoursErrorMessage = config?.messages?.errors?.maxHours?.message,

    maxHours = config.maxHours,
    maxDailyHours = config.maxDailyHours,
    maxDailyHoursWarning = config.maxDailyHoursWarning, // Exclusive for BIWEEKLY and CYCLE
    maxDays = config.maxDays,
  } = customOptions;

  const intl = useIntl();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const shiftService = new ShiftService();

  // State for weekly hours calculation
  const [hoursByWeek, setHoursByWeek] = useState<HoursByWeek>({
    hours: 0,
    formated: '',
  });

  // State for warnings - Enhanced to support warnings by day for SHIFT_WORK type
  const [warnings, setWarnings] = useState<ShiftWarningsState>({
    rangeHours: [],
    maxHours: [],
    maxDays: [],
    // Add support for warnings by specific day (used in SHIFT_WORK mode)
    byDay: {},
    byWeek: {},
    // has sunday selected
    hasSunday: false,
    maxConsecutiveActiveDays: [],
  });

  // State for errors - Enhanced to support errors by day for SHIFT_WORK type
  const [errors, setErrors] = useState<ShiftErrorsState>({
    breakTime: [],
    startBreakTime: [],
    maxHours: [],
    // Add support for errors by specific day (used in SHIFT_WORK mode)
    byDay: {},
    // Add suport by week
    byWeek: {},
  });

  const hasSundays = useMemo(() => {
    return warnings.hasSunday;
  }, [warnings]);

  /**
   * Updates the warnings state to indicate whether Sunday is selected.
   * @param {number[]} days - Array of selected days (0=Sunday).
   */
  const validateSunday = useCallback((days: Array<string | number> = []) => {
    const hasSunday = Array.isArray(days) && days.includes(7); // 7 is the value for Sunday
    setWarnings(prev => ({ ...prev, hasSunday }));
    return hasSunday;
  }, []);

  /**
   * Validates if the break time is valid
   * @param {number} timeToWork - Time to work
   * @param {number} breakTime - Break time
   * @param {number} maxHours - Maximum hours
   * @returns {boolean} - Whether validation passed
   */
  const validateBreakTime = useCallback(
    (
      timeToWork: number,
      breakTime: number | null | undefined,
      maxHours = 5,
      isActive?: boolean,
    ) => {
      if (breakTime || !isActive) return false;
      if (timeToWork > maxHours) {
        return true;
      }
      return false;
    },
    [],
  );

  /**
   * Validates if there is a 12-hour rest period between shifts
   * @param {Object} values - Current shift values containing startTime, endTime, breakTime, startBreakTime
   * @param {number} dateKey - Current day key (1=Monday, 2=Tuesday, ..., 6=Saturday, 0=Sunday)
   * @returns {boolean} - Returns true if violates the 12-hour rule (less than 12 hours rest), false otherwise
   * @description
   * This function validates the 12-hour rest rule between consecutive shifts.
   * It calculates the time difference between the previous shift's end time and current shift's start time.
   * If the difference is less than 12 hours, it returns true (violation).
   * If the difference is 12 hours or more, it returns false (complies with rule).
   * Only validates against days that are actually selected in the form.
   */
  const validateTwelveHourRule = useCallback(
    (values: ActiveDayValue, dateKey: number) => {
      // Check if current shift values are valid
      if (!values || !values.isActive || !values.startTime || !values.endTime) {
        return false;
      }

      // Get selected days from form
      const selectedDays = form?.getFieldValue('days') || [];

      // Calculate previous day key
      let previousDayKey;
      if (dateKey === 1) {
        // If current day is Monday (1), previous day is Sunday (0)
        previousDayKey = 0;
      } else if (dateKey === 0) {
        // If current day is Sunday (0), previous day is Saturday (6)
        previousDayKey = 6;
      } else {
        // For all other days, subtract 1
        previousDayKey = dateKey - 1;
      }

      // Check if the previous day is actually selected
      if (!selectedDays.includes(previousDayKey)) {
        return false; // Previous day is not selected, so no validation needed
      }

      // Get previous day shift data
      const previousShiftValues = form?.getFieldValue(`day_${previousDayKey}`);

      // Check if previous shift exists and is active
      if (!previousShiftValues || !previousShiftValues.isActive || !previousShiftValues.endTime) {
        return false; // No previous shift to compare, so no violation
      }

      // Calculate time difference between previous shift end and current shift start
      const previousEndTime = previousShiftValues.endTime;
      const currentStartTime = values.startTime;

      // Calculate the difference in hours
      let hoursDifference;

      // Since we're comparing shifts from consecutive days, the current shift
      // always starts the next day from the previous shift, so we always add 24 hours
      // to account for the day change, regardless of the hour comparison
      hoursDifference = currentStartTime.clone().add(1, 'day').diff(previousEndTime, 'hours', true);

      // Return true if violation (less than 12 hours), false if complies (12 hours or more)
      return hoursDifference < 12;
    },
    [form],
  );

  /**
   * Validates if the selected days exceed the maximum allowed
   * @param {Array} days - Array of selected days
   * @returns {boolean} - Whether validation passed
   * @description
   * This function validates the days.
   * NOTE: This function is used to validate the days rule.
   * It checks if the days selected exceed the maximum allowed.
   * If the days selected exceed the maximum allowed, it returns the error message.
   * If the days selected do not exceed the maximum allowed, it returns false.
   */
  const validateDaysRule = useCallback((_maxDays?: number) => {
    return {
      validator: (_: unknown, value?: Array<string | number>) => {
        if (value?.length > maxDays) {
          return Promise.reject(new Error(maxDaysWarningMessage));
        }
        return Promise.resolve();
      },
      warningOnly: true,
    };
  }, [maxDays, maxDaysWarningMessage]);

  const validateSundaysRule = useCallback(() => {
    return {
      validator: (_: unknown, value?: Array<string | number>) => {
        // Active resolution when it's sunday
        const hasSundays = validateSunday(value);
        if (hasSundays) {
          return Promise.reject(
            new Error(intl.formatMessage({ id: 'shifts.validation.days.sunday.warning' })),
          );
        }
        return Promise.resolve();
      },
      warningOnly: true,
    };
  }, [validateSunday, intl]);

  /**
   * Validates if the selected days exceed the maximum allowed
   * @param {Array} days - Array of selected days
   * @returns {boolean} - Whether validation passed
   * @description
   * This function validates the days.
   * It checks if the days selected exceed the maximum allowed.
   * If the days selected exceed the maximum allowed, it returns the error message.
   * If the days selected do not exceed the maximum allowed, it returns false.
   */
  const validateDays = useCallback(
    (days: Array<string | number> = []) => {
      const totalDaysSelected = days.length;
      const isMaxDays = totalDaysSelected > maxDays;

      if (isMaxDays) {
        setWarnings(prevWarnings => ({
          ...prevWarnings,
          maxDays: [{ key: 'maxDays', message: maxDaysWarningMessage }],
        }));
      } else {
        setWarnings(prevWarnings => ({
          ...prevWarnings,
          maxDays: [],
        }));
      }

      return isMaxDays;
    },
    [maxDays, maxDaysWarningMessage],
  );

  /**
   * Validates maximum weekly hours
   * @param {number} hours - Hours to validate
   * @param {number} weekKey - Week identifier (optional, used for BIWEEKLY)
   * @param {number} dayKey - Day identifier (optional, used for BIWEEKLY)
   * @description
   * This function validates the maximum weekly hours.
   * It checks if the hours worked exceed the maximum allowed weekly hours.
   * If the hours worked exceed the maximum allowed weekly hours, it returns the error message.
   * If the hours worked do not exceed the maximum allowed weekly hours, it returns false.
   * For BIWEEKLY shifts, it manages a nested structure: week -> day -> errors/warnings
   */
  const validateMaxHours = useCallback(
    (hours: number, weekKey?: string | number | null, dayKey?: string | number | null) => {
      void weekKey;
      void dayKey;
      const isMaxHours = hours > maxHours;
      const warningKey = 'maxHours';

      const warningMessage = maxHoursWarningMessage;

      if (shiftType === SHIFT_TYPES.FIXED) {
        if (isMaxHours) {
          setWarnings(prevWarnings => {
            const currentWarnings = prevWarnings?.maxHours || [];
            const hasWarning = currentWarnings.some(
              warning => warning.key === warningKey && warning.message === warningMessage,
            );

            if (hasWarning) {
              return prevWarnings;
            }

            return {
              ...prevWarnings,
              maxHours: [...currentWarnings, { key: warningKey, message: warningMessage }],
            };
          });
        } else {
          setWarnings(prevWarnings => ({
            ...prevWarnings,
            maxHours: [],
          }));
        }
      } else if (shiftType === SHIFT_TYPES.PART_TIME) {
        if (isMaxHours) {
          setErrors(prevErrors => {
            const currentErrors = prevErrors?.maxHours || [];
            const hasError = currentErrors.some(
              error => error.key === warningKey && error.message === maxHoursErrorMessage,
            );

            if (hasError) {
              return prevErrors;
            }

            return {
              ...prevErrors,
              maxHours: [...currentErrors, { key: warningKey, message: maxHoursErrorMessage }],
            };
          });
        } else {
          setErrors(prevErrors => ({
            ...prevErrors,
            maxHours: [],
          }));
        }
      } else if (shiftType === SHIFT_TYPES.BIWEEKLY) {
        if (isMaxHours) {
          setErrors(prevErrors => {
            const currentErrors = prevErrors?.maxHours || [];

            const hasError = currentErrors.some(
              error => error.key === warningKey && error.message === maxHoursErrorMessage,
            );

            if (hasError) {
              return prevErrors;
            }

            return {
              ...prevErrors,
              maxHours: [...currentErrors, { key: warningKey, message: maxHoursErrorMessage }],
            };
          });
        } else {
          // Clear errors for specific week/day combination
          setErrors(prevErrors => {
            return {
              ...prevErrors,
              maxHours: [],
            };
          });
        }
      }
    },
    [maxHours, maxHoursWarningMessage, shiftType, maxHoursErrorMessage],
  );

  const validateMaxConsecutiveActiveDays = useCallback(() => {
    if (!form) return false;
    const recurrence = form.getFieldValue('recurrence');
    if (!recurrence || !Array.isArray(recurrence.weeks) || recurrence.weeks.length === 0)
      return false;

    // Build the pattern for one complete cycle
    const cyclePattern = [];
    const debugInfo = [];

    recurrence.weeks
      .sort((a, b) => a.week - b.week)
      .forEach(week => {
        // Ensure we have all 7 days for each week (0-6)
        for (let dayIdx = 0; dayIdx <= 6; dayIdx++) {
          const dayObj = week.days?.[dayIdx];
          const isActive = dayObj && dayObj.isActive === true;
          cyclePattern.push(isActive ? 1 : 0);
          debugInfo.push({
            week: week.week,
            day: dayIdx,
            isActive,
            dayObj: dayObj ? { ...dayObj } : null,
          });
        }
      });

    // To properly check for 14+ consecutive days, we need to simulate multiple cycles
    // We'll repeat the pattern enough times to detect any possible 14+ consecutive sequence
    const cycleLength = cyclePattern.length;

    // If the cycle is shorter than 14 days, we need multiple repetitions to find 14+ consecutive
    const repetitionsNeeded = Math.ceil(14 / cycleLength) + 1; // +1 to handle wraparound cases
    const extendedPattern = [];

    for (let rep = 0; rep < repetitionsNeeded; rep++) {
      extendedPattern.push(...cyclePattern);
    }

    // Now check for consecutive days in the extended pattern
    let maxConsecutive = 0;
    let currentConsecutive = 0;

    for (let i = 0; i < extendedPattern.length; i++) {
      if (extendedPattern[i] === 1) {
        currentConsecutive += 1;
        maxConsecutive = Math.max(maxConsecutive, currentConsecutive);

        // Early exit if we've already found 14+ consecutive days
        if (maxConsecutive >= 14) {
          break;
        }
      } else {
        currentConsecutive = 0;
      }
    }

    if (maxConsecutive >= 14) {
      setWarnings(prevWarnings => ({
        ...prevWarnings,
        maxConsecutiveActiveDays: [
          { key: 'maxConsecutiveActiveDays', message: maxConsecutiveActiveDaysWarning },
        ],
      }));
    } else {
      setWarnings(prevWarnings => ({
        ...prevWarnings,
        maxConsecutiveActiveDays: [],
      }));
    }

    return maxConsecutive >= 14;
  }, [form, maxConsecutiveActiveDaysWarning]);

  /**
   * Validates overtime by day
   * @param {Moment} startTime - Start time
   * @param {Moment} endTime - End time
   * @param {number} breakTime - Break time in minutes
   * @returns {string|false} - Error message or false if valid
   * @description
   * This function validates the overtime by day.
   * It checks if the hours worked exceed the maximum allowed daily hours.
   * If the hours worked exceed the maximum allowed daily hours, it returns the error message.
   * If the hours worked do not exceed the maximum allowed daily hours, it returns false.
   */
  const validateOvertimeByDay = useCallback(
    (startTime: Moment | null, endTime: Moment | null, breakTime = 0) => {
      const pMaxDailyHours = ([SHIFT_TYPES.BIWEEKLY, SHIFT_TYPES.CYCLE] as string[]).includes(
        shiftType,
      )
        ? maxDailyHoursWarning
        : maxDailyHours;
      if (!startTime || !endTime) return false;

      let hours = endTime?.diff(startTime, 'minutes') / 60;
      hours = Number(hours.toFixed(2));
      const hoursWithBreak = hours - breakTime / 60;

      if (hoursWithBreak > pMaxDailyHours) {
        return maxDailyHoursWarningMessage;
      }
      return false;
    },
    [maxDailyHoursWarningMessage, maxDailyHours, maxDailyHoursWarning, shiftType],
  );

  /**
   * Validates break time requirement for shifts that require meal breaks
   * @param {Object} values - Shift values
   * @returns {Object} - Validation results
   */
  const validateBreakTimeRequirement = useCallback(
    (values: ActiveDayValue) => {
      const { startTime, endTime, breakTime, startBreakTime, isActive } = values;

      const breakTimeError = validateBreakTime(
        endTime!.diff(startTime!, 'minutes') / 60,
        breakTime,
        5,
        isActive,
      );

      const startBreakTimeError =
        isActive !== false && isStartBreakTimeMissing(breakTime, startBreakTime)
          ? intl.formatMessage({ id: 'form.required.msg' })
          : null;

      return {
        breakTime: breakTimeError ? [{ key: 'breakTime', message: breakTimeErrorMessage }] : [],
        startBreakTime: startBreakTimeError
          ? [{ key: 'startBreakTime', message: startBreakTimeError }]
          : [],
      };
    },
    [validateBreakTime, intl, breakTimeErrorMessage],
  );

  /**
   * Validates twelve hour rule between shifts
   * @param {Object} values - Shift values
   * @param {string} dayKey - Day key
   * @returns {Object} - Validation results
   */
  const validateTwelveHourRuleRequirement = useCallback(
    (values: ActiveDayValue, dayKey: number) => {
      const ItIsIntoTwelveRange = validateTwelveHourRule(values, dayKey);

      return {
        twelveHourRule: ItIsIntoTwelveRange
          ? [
              {
                key: 'twelveHourRule',
                message: intl.formatMessage({ id: 'shifts.validation.hours.twelveHourRule.error' }),
              },
            ]
          : [],
      };
    },
    [validateTwelveHourRule, intl],
  );

  /**
   * Validates overtime hours for the day
   * @param {Object} values - Shift values
   * @returns {Object} - Validation results
   */
  const validateOvertimeRequirement = useCallback(
    (values: ActiveDayValue) => {
      const { startTime, endTime, breakTime } = values;
      const maxHoursWarning = validateOvertimeByDay(startTime, endTime, breakTime);
      const rangeHours = maxHoursWarning ? [{ key: 'maxHours', message: maxHoursWarning }] : [];
      return { rangeHours };
    },
    [validateOvertimeByDay],
  );

  /**
   * Validator for FIXED shift type
   * @param {Object} values - Shift values
   * @param {string} dayKey - Day key
   * @returns {Object} - Validation results { warnings, errors, storageType }
   */
  const validateFixedShift = useCallback(
    (values: ActiveDayValue, dayKey?: string | number | null): ShiftValidatorResult => {
      const warnings = validateOvertimeRequirement(values);
      const breakTimeErrors = validateBreakTimeRequirement(values);

      return {
        warnings,
        errors: { ...breakTimeErrors },
        storageType: 'global', // FIXED uses global storage
        dayKey,
      };
    },
    [validateOvertimeRequirement, validateBreakTimeRequirement],
  );

  /**
   * Validator for PART_TIME shift type
   * @param {Object} values - Shift values
   * @param {string} dayKey - Day key
   * @returns {Object} - Validation results { warnings, errors, storageType }
   */
  const validatePartTimeShift = useCallback(
    (values: ActiveDayValue, dayKey?: string | number | null): ShiftValidatorResult => {
      const warnings = validateOvertimeRequirement(values);

      return {
        warnings,
        errors: {},
        storageType: 'global', // PART_TIME uses global storage
        dayKey,
      };
    },
    [validateOvertimeRequirement],
  );

  /**
   * Validator for SHIFT_WORK shift type
   * @param {Object} values - Shift values
   * @param {string} dayKey - Day key
   * @returns {Object} - Validation results { warnings, errors, storageType, dayKey }
   */
  const validateWorkdayShift = useCallback(
    (values: ActiveDayValue, dayKey?: string | number | null): ShiftValidatorResult => {
      const warnings = validateOvertimeRequirement(values);
      const twelveHourErrors = validateTwelveHourRuleRequirement(values, dayKey as number);

      // SHIFT_WORK typically doesn't require break time validation
      const startBreakTimeError =
        values.isActive !== false &&
        isStartBreakTimeMissing(values.breakTime, values.startBreakTime)
          ? intl.formatMessage({ id: 'form.required.msg' })
          : null;

      return {
        warnings,
        errors: {
          startBreakTime: startBreakTimeError
            ? [{ key: 'startBreakTime', message: startBreakTimeError }]
            : [],
          rangeHours: [...twelveHourErrors.twelveHourRule],
        },
        storageType: dayKey ? 'byDay' : 'global', // SHIFT_WORK uses byDay storage when dayKey is provided
        dayKey,
      };
    },
    [validateOvertimeRequirement, validateTwelveHourRuleRequirement, intl],
  );

  /**
   * Validator for CYCLE and BIWEEKLY shift types
   * @param {Object} values - Shift values
   * @param {string} dayKey - Day key
   * @returns {Object} - Validation results { warnings, errors, storageType }
   */
  const validateBiWeeklyShift = useCallback(
    (
      values: ActiveDayValue,
      dayKey?: string | number | null,
      weekKey?: string | number | null,
    ): ShiftValidatorResult => {
      const warnings = validateOvertimeRequirement(values);
      const twelveHourErrors = validateTwelveHourRuleRequirement(values, dayKey as number);
      const breakTimeErrors = validateBreakTimeRequirement(values);

      return {
        warnings: {
          ...warnings,
          // ...hasMoreThan14ConsecutiveActiveDaysWarning,
        },
        errors: {
          ...twelveHourErrors,
          ...breakTimeErrors,
        },
        storageType: dayKey && !weekKey ? 'byDay' : weekKey ? 'byWeek' : 'global', //  uses byDay storage when dayKey is provided
        dayKey,
        weekKey,
      };
    },
    [validateOvertimeRequirement, validateTwelveHourRuleRequirement, validateBreakTimeRequirement],
  );

  /**
   * Gets the appropriate validator function based on shift type
   * @returns {Function} - Validator function for the current shift type
   */
  const getShiftValidator = useCallback((): ((
    values: ActiveDayValue,
    dayKey?: string | number | null,
    weekKey?: string | number | null,
  ) => ShiftValidatorResult) => {
    switch (shiftType) {
      case SHIFT_TYPES.FIXED:
        return validateFixedShift;
      case SHIFT_TYPES.PART_TIME:
        return validatePartTimeShift;
      case SHIFT_TYPES.SHIFT_WORK:
        return validateWorkdayShift;
      case SHIFT_TYPES.CYCLE:
      case SHIFT_TYPES.BIWEEKLY:
        return validateBiWeeklyShift;
      default:
        return validateFixedShift; // Default fallback
    }
  }, [
    shiftType,
    validateFixedShift,
    validatePartTimeShift,
    validateWorkdayShift,
    validateBiWeeklyShift,
  ]);

  /**
   * Validates active day warnings and errors (now intelligent and segmented by shift type)
   * @param {Object} values - Form values containing startTime, endTime, breakTime, startBreakTime
   * @param {string} dayKey - Optional day key for day-specific warnings (used in SHIFT_WORK mode)
   */
  const validateActiveDay = useCallback(
    (
      values: ActiveDayValue,
      dayKey: string | number | null = null,
      weekKey: string | number | null = null,
    ) => {
      const { startTime, endTime } = values || {};
      if (!startTime || !endTime) return;

      // Get the appropriate validator for this shift type
      const validator = getShiftValidator();
      const {
        warnings: newWarnings,
        errors: newErrors,
        storageType,
        dayKey: validatorDayKey,
        weekKey: validatorWeekKey,
      } = validator(values, dayKey, weekKey);

      // Store warnings and errors based on validator's preference
      if (storageType === 'byDay' && validatorDayKey) {
        // Store by specific day
        setWarnings(prevWarnings => ({
          ...prevWarnings,
          byDay: {
            ...prevWarnings.byDay,
            [validatorDayKey]: newWarnings,
          },
        }));

        setErrors(prevErrors => ({
          ...prevErrors,
          byDay: {
            ...prevErrors.byDay,
            [validatorDayKey]: newErrors,
          },
        }));
      } else if (storageType === 'byWeek' && validatorWeekKey) {
        // Store by specific week
        setWarnings(prevWarnings => ({
          ...prevWarnings,
          byWeek: {
            ...prevWarnings.byWeek,
            [validatorWeekKey]: {
              ...prevWarnings?.byWeek?.[validatorWeekKey],
              [validatorDayKey]: newWarnings,
            },
          },
        }));

        setErrors(prevErrors => ({
          ...prevErrors,
          byWeek: {
            ...prevErrors.byWeek,
            [validatorWeekKey]: {
              ...prevErrors?.byWeek?.[validatorWeekKey],
              [validatorDayKey]: newErrors,
            },
          },
        }));
      } else {
        // Store globally
        setWarnings(prevWarnings => ({
          ...prevWarnings,
          ...newWarnings,
        }));

        setErrors(prevErrors => ({
          ...prevErrors,
          ...newErrors,
        }));
      }
    },
    [getShiftValidator],
  );

  /**
   * Calculates total shift hours and validates them
   * @param {Array} days - Selected days
   * @param {Object} recurrence - Recurrence object
   * @param {number} weekKey - Week identifier (optional, used for BIWEEKLY)
   * @param {number} dayKey - Day identifier (optional, used for BIWEEKLY)
   */
  const calculateTotalShiftHours = useCallback(
    (
      days: Array<string | number>,
      recurrence: unknown,
      weekKey: string | number | null = null,
      dayKey: string | number | null = null,
    ) => {
      const { hours, formated } = ShiftService.calculateTotalShiftHours(days, recurrence);

      setHoursByWeek(prevState => {
        if (prevState.hours !== hours) return { hours, formated };
        return prevState;
      });

      validateMaxHours(hours, weekKey, dayKey);
    },
    [validateMaxHours],
  );

  /**
   * Handles form values change
   * @param {Object} changedFields - Changed form fields
   * @param {Object} allFields - All form fields
   * @param {Function} setFieldValue - Function to set form field value
   */
  const handleFormValuesChange = useCallback(
    (
      changedFields: Record<string, unknown>,
      allFields: { days?: Array<string | number>; recurrence?: unknown },
      _setFieldValue?: unknown,
    ) => {
      const [key] = Object.keys(changedFields);
      if (key === 'regulationName') return;

      calculateTotalShiftHours(allFields.days, allFields.recurrence);
    },
    [calculateTotalShiftHours],
  );

  /**
   * Validates if there are any active errors
   * @returns {boolean} - Whether there are any errors
   */
  const hasErrors = useCallback(() => {
    const generalError = Object.values(errors).some(error => {
      if (Array.isArray(error)) {
        return error.length > 0;
      }
      return false;
    });
    const byDayError = Object.values(errors?.byDay).some(error =>
      Object.values(error).some(error => error.length > 0),
    );
    const byWeekError = Object.values(errors?.byWeek).some(weekObj =>
      Object.values(weekObj).some(dayObj =>
        Object.values(dayObj).some(arr => Array.isArray(arr) && arr.length > 0),
      ),
    );

    return generalError || byDayError || byWeekError;
  }, [errors]);

  /**
   * Validates if there are any active warnings
   * @returns {boolean} - Whether there are any warnings
   */
  const hasWarnings = useCallback(() => {
    const generalWarnings = Object.values(warnings).some(
      warning => Array.isArray(warning) && warning.length > 0,
    );

    const byDayWarnings = Object.values(warnings?.byDay).some(dayWarnings =>
      Object.values(dayWarnings).some(warning => Array.isArray(warning) && warning.length > 0),
    );

    const byWeekWarnings = Object.values(warnings?.byWeek || {}).some(weekObj =>
      Object.values(weekObj || {}).some(dayObj =>
        Object.values(dayObj || {}).some(arr => Array.isArray(arr) && arr.length > 0),
      ),
    );

    return generalWarnings || byDayWarnings || byWeekWarnings || hasSundays;
  }, [warnings, hasSundays]);

  const hasWarningsByDay = useCallback(() => {
    const byDayWarnings = Object.values(warnings?.byDay).some(warning =>
      Object.values(warning).some(warning => (warning as ValidationMessage[]).length > 0),
    );

    const hasSundaysWarning = warnings.hasSunday;

    return byDayWarnings || hasSundaysWarning;
  }, [warnings]);

  /**
   * Day / schedule-pattern warnings that require a regulatory resolution (Dirección del Trabajo).
   * Excludes hour-only warnings (e.g. max weekly or daily hours).
   */
  const hasDaySelectionRegulatoryWarning = useCallback(() => {
    if (warnings.hasSunday) return true;
    if (Array.isArray(warnings.maxDays) && warnings.maxDays.length > 0) return true;
    if (
      shiftType === SHIFT_TYPES.BIWEEKLY &&
      Array.isArray(warnings.maxConsecutiveActiveDays) &&
      warnings.maxConsecutiveActiveDays.length > 0
    ) {
      return true;
    }
    return false;
  }, [warnings, shiftType]);

  /**
   * Gets warnings for a specific day (used in SHIFT_WORK mode)
   * @param {string} dayKey - The day key to get warnings for
   * @returns {Object} - Warnings object for the specific day
   */
  const getWarningsForDay = useCallback(
    (dayKey: string | number) => {
      if (shiftType === SHIFT_TYPES.SHIFT_WORK && warnings.byDay[dayKey]) {
        return warnings.byDay[dayKey];
      }
      // Fallback to global warnings for other shift types or if no day-specific warnings exist
      return {
        rangeHours: warnings.rangeHours,
        maxHours: warnings.maxHours,
        maxDays: warnings.maxDays,
      };
    },
    [shiftType, warnings],
  );

  /**
   * Gets errors for a specific day (used in SHIFT_WORK mode)
   * @param {string} dayKey - The day key to get errors for
   * @returns {Object} - Errors object for the specific day
   */
  const getErrorsForDay = useCallback(
    (dayKey: string | number) => {
      if (shiftType === SHIFT_TYPES.SHIFT_WORK && errors.byDay[dayKey]) {
        return errors.byDay[dayKey];
      }
      // Fallback to global errors for other shift types or if no day-specific errors exist
      return {
        breakTime: errors.breakTime,
        startBreakTime: errors.startBreakTime,
        maxHours: errors.maxHours,
      };
    },
    [shiftType, errors],
  );

  /**
   * Gets errors for a specific week/day combination (used in BIWEEKLY mode)
   * @param {number} weekKey - The week key to get errors for
   * @param {number} dayKey - The day key to get errors for
   * @returns {Object} - Errors object for the specific week/day combination
   */
  const getErrorsForWeekDay = useCallback(
    (weekKey: string | number, dayKey: string | number) => {
      if (shiftType === SHIFT_TYPES.BIWEEKLY && errors.byWeek?.[weekKey]?.[dayKey]) {
        return errors.byWeek[weekKey][dayKey];
      }
      // Fallback to empty array if no specific week/day errors exist
      return [];
    },
    [shiftType, errors],
  );

  /**
   * Gets warnings for a specific week/day combination (used in BIWEEKLY mode)
   * @param {number} weekKey - The week key to get warnings for
   * @param {number} dayKey - The day key to get warnings for
   * @returns {Object} - Warnings object for the specific week/day combination
   */
  const getWarningsForWeekDay = useCallback(
    (weekKey: string | number, dayKey: string | number) => {
      if (shiftType === SHIFT_TYPES.BIWEEKLY && warnings.byWeek?.[weekKey]?.[dayKey]) {
        return warnings.byWeek[weekKey][dayKey];
      }
      // Fallback to empty array if no specific week/day warnings exist
      return [];
    },
    [shiftType, warnings],
  );

  /**
   * Resets all validation states
   */
  const resetValidations = useCallback(() => {
    setHoursByWeek({
      hours: 0,
      formated: '',
    });
    setWarnings({
      rangeHours: [],
      maxHours: [],
      maxDays: [],
      byDay: {},
      byWeek: {},
      hasSunday: false,
      maxConsecutiveActiveDays: [],
    });
    setErrors({
      breakTime: [],
      startBreakTime: [],
      maxHours: [],
      byDay: {},
      byWeek: {},
    });
  }, []);

  return {
    // State
    hoursByWeek,
    warnings,
    errors,

    // Functions
    validateSunday,
    validateDays,
    validateDaysRule,
    validateActiveDay,
    validateOvertimeByDay,
    validateTwelveHourRule, // Add the new function to exports
    validateMaxHours,
    validateMaxConsecutiveActiveDays,
    validateSundaysRule,
    calculateTotalShiftHours,
    handleFormValuesChange,
    hasErrors,
    hasWarnings,
    hasSundays,
    hasWarningsByDay,
    hasDaySelectionRegulatoryWarning,
    resetValidations,
    getWarningsForDay, // New function to get warnings for specific day
    getErrorsForDay, // New function to get errors for specific day
    getErrorsForWeekDay, // New function to get errors for specific week/day combination (BIWEEKLY)
    getWarningsForWeekDay, // New function to get warnings for specific week/day combination (BIWEEKLY)

    // Utilities
    shiftService,
  };
};
